import { useEffect, useState } from "react";

import { Hint } from "@/design-system/Hint.tsx";
import type { MapHandle } from "@/map/MapCanvas.tsx";
import { cn } from "@/design-system/cn.ts";

/**
 * The map scale, in React rather than MapLibre's ScaleControl.
 *
 * The built-in sets an inline pixel width equal to the bar length, which clips
 * its own label whenever the rounded distance is shorter than the text ("1000
 * ft" in a 46px box). Owning it here separates the bar's width from the chip's,
 * and lets the chip carry a full-strength surface so it stays legible over a
 * dark and a light basemap alike — the thing behind it is the *map*, which
 * changes independently of the theme.
 */

const MAX_BAR_PX = 88;
const FEET_PER_METER = 3.28084;
const FEET_PER_MILE = 5280;

const FOOT_STEPS = [10, 20, 50, 100, 200, 500, 1000, 2000];
const MILE_STEPS = [1, 2, 5, 10, 20, 50, 100, 200];

/** The largest round distance that still fits inside `maxMeters`. */
function niceScale(maxMeters: number): { label: string; meters: number } {
  const maxFeet = maxMeters * FEET_PER_METER;
  if (maxFeet < FEET_PER_MILE) {
    const ft = FOOT_STEPS.filter((s) => s <= maxFeet).pop() ?? FOOT_STEPS[0];
    return { label: `${ft.toLocaleString("en-US")} ft`, meters: ft / FEET_PER_METER };
  }
  const maxMiles = maxFeet / FEET_PER_MILE;
  const mi = MILE_STEPS.filter((s) => s <= maxMiles).pop() ?? MILE_STEPS[0];
  return { label: `${mi} mi`, meters: (mi * FEET_PER_MILE) / FEET_PER_METER };
}

export function ScaleBar({
  handle,
  ready,
  className,
  style,
}: {
  handle: React.RefObject<MapHandle | null>;
  ready: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [scale, setScale] = useState<{ label: string; widthPx: number } | null>(null);

  useEffect(() => {
    const map = handle.current?.map;
    if (!map) return;

    const recompute = () => {
      const y = map.getCanvas().clientHeight / 2;
      // Ground distance covered by MAX_BAR_PX at the viewport's centre latitude.
      const maxMeters = map.unproject([0, y]).distanceTo(map.unproject([MAX_BAR_PX, y]));
      if (!Number.isFinite(maxMeters) || maxMeters <= 0) return;
      const { label, meters } = niceScale(maxMeters);
      setScale({ label, widthPx: Math.round((meters / maxMeters) * MAX_BAR_PX) });
    };

    recompute();
    map.on("move", recompute);
    map.on("resize", recompute);
    return () => {
      map.off("move", recompute);
      map.off("resize", recompute);
    };
  }, [handle, ready]);

  if (!scale) return null;

  return (
    // Placed below rather than beside: this control is pinned to the top-right,
    // and a left-side tooltip on a narrow phone has too little room either side
    // to shift or flip into. A perpendicular side gets the whole viewport width.
    <Hint
      label="Map scale"
      detail="Distance across the bar at the centre of the view."
      side="bottom"
      align="end"
    >
      <button
        type="button"
        aria-label={`Map scale: ${scale.label}`}
        className={cn(
          // Opaque, bordered and shadowed on purpose: the surface behind this is
          // whichever basemap is loaded, not the app's own background.
          "pointer-events-auto flex cursor-default flex-col items-start gap-1 rounded-lg border border-rule-bright bg-ink-900 px-2 py-1.5 shadow-[0_8px_20px_-10px_var(--console-shadow)] backdrop-blur",
          className,
        )}
        style={style}
      >
        <span className="tnum font-mono text-[9px] leading-none font-medium tracking-[0.1em] text-chalk-dim">
          {scale.label}
        </span>
        <span className="relative h-[5px]" style={{ width: MAX_BAR_PX }}>
          {/* Full-width rail, then the measured span drawn over it — the rail
              gives the number something to be measured against. */}
          <span className="absolute inset-x-0 bottom-0 h-px bg-rule" />
          <span
            className="absolute bottom-0 left-0 border-r border-l border-chalk-mute"
            style={{ width: scale.widthPx, height: 5 }}
          >
            <span className="absolute inset-x-0 bottom-0 h-px bg-chalk-mute" />
          </span>
        </span>
      </button>
    </Hint>
  );
}
